// CurrentSensorGraphs.js (alphatech)
// Same data pipeline as Aquabrim (endpoint api/sensor/currentSensorlogs, merged
// by IST HH:mm), presented in alphatech's flat slate style with recharts.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { FaDownload } from 'react-icons/fa';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import html2canvas from 'html2canvas';
import { API_BASE_URL } from '../../../config/config';
import { SLATE, PHASE, SectionCard, PillTabs, SeriesChip } from './sensorUi';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(customParseFormat);

const IST = 'Asia/Kolkata';
const getToday = () => dayjs().tz(IST).format('DD-MM-YYYY');

const mergeByMinute = (logs, apply) => {
  const merged = {};
  (logs || []).forEach((log) => {
    const ts = dayjs.utc(log.timestamp).tz(IST).format('HH:mm');
    if (!merged[ts]) merged[ts] = { timestamp: ts };
    apply(merged[ts], log);
  });
  return Object.values(merged).sort((a, b) =>
    dayjs(a.timestamp, 'HH:mm').diff(dayjs(b.timestamp, 'HH:mm')),
  );
};

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div
      style={{
        background: 'rgba(255,255,255,0.97)',
        border: `1px solid ${SLATE.line}`,
        borderRadius: 10,
        boxShadow: '0 6px 18px rgba(15,23,42,0.12)',
        padding: '8px 10px',
        minWidth: 130,
      }}
    >
      <div style={{ fontWeight: 800, color: SLATE.ink, fontSize: '0.72rem', marginBottom: 4 }}>{label}</div>
      {payload.map((e) => (
        <div key={e.dataKey} style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 6, color: SLATE.ink2, fontSize: '0.72rem' }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: e.color }} />
            {e.name}
          </span>
          <span style={{ fontWeight: 800, color: e.color, fontSize: '0.72rem', fontVariantNumeric: 'tabular-nums' }}>
            {Number(e.value).toFixed(2)}
          </span>
        </div>
      ))}
    </div>
  );
};

const axisTick = { fontSize: 10, fill: SLATE.muted };

const CurrentSensorGraphs = ({ sensor }) => {
  const deviceCode = sensor?.device_code;
  const subId = sensor?.sensor_sub_id;
  const today = getToday();

  const [tab, setTab] = useState('current');
  const [currentData, setCurrentData] = useState([]);
  const [voltageData, setVoltageData] = useState([]);
  const [powerData, setPowerData] = useState([]);
  const [downloading, setDownloading] = useState(false);

  const [showAvgC, setShowAvgC] = useState(false);
  const [showAvgV, setShowAvgV] = useState(false);
  const [phaseC, setPhaseC] = useState({ R: true, Y: true, B: true });
  const [phaseV, setPhaseV] = useState({ R: true, Y: true, B: true });

  const chartRef = useRef(null);

  useEffect(() => {
    if (!deviceCode || !subId) return undefined;

    const fetchCurrent = async () => {
      try {
        const { data } = await axios.get(`${API_BASE_URL}api/sensor/currentSensorlogs`, {
          params: { deviceCode, subId, fromDate: today, toDate: today, logTypes: showAvgC ? 'current,average' : 'current' },
        });
        setCurrentData(mergeByMinute(data.logs, (row, log) => {
          if (log.logType === 'current') {
            if (log.setting.includes('Phase R')) row.phaseR = log.value;
            else if (log.setting.includes('Phase Y')) row.phaseY = log.value;
            else if (log.setting.includes('Phase B')) row.phaseB = log.value;
          } else if (log.logType === 'average' && log.setting === 'Average Current') {
            row.averageCurrent = log.value;
          }
        }));
      } catch (_) { /* keep last data */ }
    };

    const fetchVoltage = async () => {
      try {
        const { data } = await axios.get(`${API_BASE_URL}api/sensor/currentSensorlogs`, {
          params: { deviceCode, subId, fromDate: today, toDate: today, logTypes: showAvgV ? 'voltage,average' : 'voltage' },
        });
        setVoltageData(mergeByMinute(data.logs, (row, log) => {
          if (log.logType === 'voltage') {
            if (log.setting.includes('Phase R')) row.voltageR = log.value;
            else if (log.setting.includes('Phase Y')) row.voltageY = log.value;
            else if (log.setting.includes('Phase B')) row.voltageB = log.value;
          } else if (log.logType === 'average' && log.setting === 'Average Voltage') {
            row.averageVoltage = log.value;
          }
        }));
      } catch (_) { /* keep last data */ }
    };

    const fetchPower = async () => {
      try {
        const { data } = await axios.get(`${API_BASE_URL}api/sensor/currentSensorlogs`, {
          params: { deviceCode, subId, fromDate: today, toDate: today, logTypes: 'power' },
        });
        setPowerData(mergeByMinute(data.logs, (row, log) => {
          if (log.setting === 'Active Power') row.activePower = log.value;
          else if (log.setting === 'Reactive Power') row.reactivePower = log.value;
        }));
      } catch (_) { /* keep last data */ }
    };

    const run = () => { fetchCurrent(); fetchVoltage(); fetchPower(); };
    run();
    const id = setInterval(run, 30000);
    return () => clearInterval(id);
  }, [deviceCode, subId, today, showAvgC, showAvgV]);

  const download = async () => {
    if (!chartRef.current) return;
    try {
      setDownloading(true);
      const canvas = await html2canvas(chartRef.current, { scale: window.devicePixelRatio, backgroundColor: '#ffffff' });
      const link = document.createElement('a');
      link.href = canvas.toDataURL('image/png');
      link.download = `${tab}_graph_${today.replace(/-/g, '')}.png`;
      link.click();
    } catch (_) { /* ignore */ } finally {
      setDownloading(false);
    }
  };

  const yDomain = useMemo(() => [(min) => Math.floor(min * 0.9), (max) => Math.ceil(max * 1.1)], []);

  const activeData = tab === 'current' ? currentData : tab === 'voltage' ? voltageData : powerData;
  const hasData = activeData.length > 0;

  const downloadBtn = (
    <button
      onClick={download}
      disabled={downloading || !hasData}
      title="Download graph"
      style={{
        width: 34, height: 34, borderRadius: 9, border: 'none', cursor: hasData ? 'pointer' : 'not-allowed',
        background: hasData ? SLATE.ink : '#cbd5e1', color: '#fff',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
    >
      <FaDownload size={12} />
    </button>
  );

  return (
    <SectionCard
      icon={<span style={{ fontWeight: 900, color: PHASE.B }}>~</span>}
      title="Sensor Graphs"
      subtitle={`Today · ${today}`}
      right={downloadBtn}
    >
      <div style={{ marginBottom: 12 }}>
        <PillTabs
          value={tab}
          onChange={setTab}
          options={[
            { value: 'current', label: 'Current' },
            { value: 'voltage', label: 'Voltage' },
            { value: 'power', label: 'Power' },
          ]}
        />
      </div>

      {/* Series toggles */}
      {tab === 'current' && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
          <SeriesChip label="Phase R" active={phaseC.R} color={PHASE.R} onClick={() => setPhaseC((p) => ({ ...p, R: !p.R }))} />
          <SeriesChip label="Phase Y" active={phaseC.Y} color={PHASE.Y} onClick={() => setPhaseC((p) => ({ ...p, Y: !p.Y }))} />
          <SeriesChip label="Phase B" active={phaseC.B} color={PHASE.B} onClick={() => setPhaseC((p) => ({ ...p, B: !p.B }))} />
          <SeriesChip label="Average" active={showAvgC} color={PHASE.avg} onClick={() => setShowAvgC((v) => !v)} />
        </div>
      )}
      {tab === 'voltage' && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
          <SeriesChip label="Phase R" active={phaseV.R} color={PHASE.R} onClick={() => setPhaseV((p) => ({ ...p, R: !p.R }))} />
          <SeriesChip label="Phase Y" active={phaseV.Y} color={PHASE.Y} onClick={() => setPhaseV((p) => ({ ...p, Y: !p.Y }))} />
          <SeriesChip label="Phase B" active={phaseV.B} color={PHASE.B} onClick={() => setPhaseV((p) => ({ ...p, B: !p.B }))} />
          <SeriesChip label="Average" active={showAvgV} color={PHASE.avg} onClick={() => setShowAvgV((v) => !v)} />
        </div>
      )}

      {hasData ? (
        <div ref={chartRef} style={{ width: '100%' }}>
          <ResponsiveContainer width="100%" height={320}>
            <LineChart data={activeData} margin={{ top: 12, right: 16, left: -8, bottom: 6 }}>
              <CartesianGrid strokeDasharray="3 4" stroke={SLATE.line2} vertical={false} />
              <XAxis dataKey="timestamp" tick={axisTick} tickMargin={6} axisLine={{ stroke: SLATE.line }} tickLine={false} interval="preserveStartEnd" />
              <YAxis tick={axisTick} domain={tab === 'power' ? [(min) => Math.floor(min * 0.2), (max) => Math.ceil(max * 1.1)] : yDomain} width={38} axisLine={false} tickLine={false} />
              <Tooltip content={<ChartTooltip />} />
              <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: '0.72rem', paddingTop: 6 }} />

              {tab === 'current' && phaseC.R && <Line type="monotone" dataKey="phaseR" name="Phase R" stroke={PHASE.R} strokeWidth={2} dot={false} connectNulls isAnimationActive={false} />}
              {tab === 'current' && phaseC.Y && <Line type="monotone" dataKey="phaseY" name="Phase Y" stroke={PHASE.Y} strokeWidth={2} dot={false} connectNulls isAnimationActive={false} />}
              {tab === 'current' && phaseC.B && <Line type="monotone" dataKey="phaseB" name="Phase B" stroke={PHASE.B} strokeWidth={2} dot={false} connectNulls isAnimationActive={false} />}
              {tab === 'current' && showAvgC && <Line type="monotone" dataKey="averageCurrent" name="Average" stroke={PHASE.avg} strokeWidth={2.4} strokeDasharray="6 3" dot={false} connectNulls isAnimationActive={false} />}

              {tab === 'voltage' && phaseV.R && <Line type="monotone" dataKey="voltageR" name="Phase R" stroke={PHASE.R} strokeWidth={2} dot={false} connectNulls isAnimationActive={false} />}
              {tab === 'voltage' && phaseV.Y && <Line type="monotone" dataKey="voltageY" name="Phase Y" stroke={PHASE.Y} strokeWidth={2} dot={false} connectNulls isAnimationActive={false} />}
              {tab === 'voltage' && phaseV.B && <Line type="monotone" dataKey="voltageB" name="Phase B" stroke={PHASE.B} strokeWidth={2} dot={false} connectNulls isAnimationActive={false} />}
              {tab === 'voltage' && showAvgV && <Line type="monotone" dataKey="averageVoltage" name="Average" stroke={PHASE.avg} strokeWidth={2.4} strokeDasharray="6 3" dot={false} connectNulls isAnimationActive={false} />}

              {tab === 'power' && <Line type="monotone" dataKey="activePower" name="Active (kW)" stroke={PHASE.active} strokeWidth={2.2} dot={false} connectNulls isAnimationActive={false} />}
              {tab === 'power' && <Line type="monotone" dataKey="reactivePower" name="Reactive (kVAR)" stroke={PHASE.reactive} strokeWidth={2.2} dot={false} connectNulls isAnimationActive={false} />}
            </LineChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div style={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', color: SLATE.faint, fontSize: '0.82rem' }}>
          No {tab} log data available for today.
        </div>
      )}
    </SectionCard>
  );
};

export default CurrentSensorGraphs;
